import { baseApi, transportRequest, type TransportError, type TransportRequest } from '@krp/client-runtime'

import { searchRun } from '../model/searchRun'
import type { SearchHealth, SearchResponse, SearchTiming, SearchTimingSummary } from '../model/types'
import { decodeSearchHealth, decodeSearchTiming, decodeSearchTimingSummary } from './contracts'
import { peekFrame } from './frames'
import { decodeStreamEvent, type StreamEvent } from './progressEvents'
import { PLATFORM_SEARCH_URLS } from './urls'

/**
 * Поиск по фото через общий ProductId. Запрос — multipart (`photo`, `limit`, `resourceIds[]`); кадр берётся из реестра `frames` по
 * `frameId`. Области не передаём: сервер без неё ищет в боевом каталоге (RP-FB27).
 *
 * Ответ - ПОТОК событий этапов (NDJSON, RP-FB43): они идут в `searchRun` (экран показывает ход работы), а итог мутации - тот же
 * `SearchResponse`, что отдавал прежний синхронный ответ. Загрузка кадра идёт через XMLHttpRequest, чтобы показать настоящий прогресс
 * отправки. Поиск - не команда с побочным эффектом, но и не кэшируемый запрос: каждый кадр - новый вопрос, поэтому мутация. Автоповтора
 * нет: повтор - кнопка «Повторить».
 */
export interface PlatformSearchPhotoArgs {
  frameId: string
  limit: number
  resourceIds?: readonly string[]
}

/** Поиск по фото может идти долго (холодная модель, слабая сеть): запас больше обычного таймаута, но не бесконечный. */
export const SEARCH_TIMEOUT_MS = 200_000

function streamFailure(problem: Extract<StreamEvent, { type: 'error' }>['problem']): TransportError {
  const kind = problem.status === 503 || problem.status === 502 || problem.status === 504 ? 'unavailable'
    : problem.status === 403 ? 'forbidden'
      : problem.status === 400 ? 'validation'
        : 'http'
  return {
    kind,
    status: problem.status,
    code: problem.code,
    message: problem.message,
    correlationId: problem.correlationId ?? '',
    retryable: kind === 'unavailable',
    ...(problem.components.length > 0 ? { details: { components: problem.components } } : {}),
  }
}

/** Подтверждение «это он»: товар и прочитанные на снимке слова (только полосы `key` и `medium` сервер запоминает). `operationId` — ключ повтора. */
export interface PlatformSearchConfirmArgs {
  operationId: string
  productId: string
  words: readonly { text: string; band: string }[]
}

export interface PlatformSearchConfirmResult {
  replayed: boolean
  phrases: number
}

/**
 * Действие человека над ответом поиска по фото (RP-FB77): `searchId` из ответа, слово действия и товар (для «Это он», «Нет, другой», «Прикрепить»).
 * Сервер ставит кандидатам этого поиска метки для обучения. `operationId` — ключ повтора.
 */
export type SearchFeedbackAction = 'confirm' | 'reject' | 'none-of-these' | 'attach' | 'create'

export interface PlatformSearchFeedbackArgs {
  operationId: string
  searchId: string
  action: SearchFeedbackAction
  productId?: string | undefined
}

export interface PlatformSearchFeedbackResult {
  replayed: boolean
  /** `false` — поиска уже нет в журнале (истёк срок хранения): меток нет, это не ошибка. */
  found: boolean
  labeled: number
}

export const PLATFORM_SEARCH_TAG = 'PlatformSearch' as const

export const searchApi = baseApi.enhanceEndpoints({ addTagTypes: [PLATFORM_SEARCH_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    platformSearchPhoto: build.mutation<SearchResponse, PlatformSearchPhotoArgs>({
      queryFn: async ({ frameId, limit, resourceIds }, api) => {
        const frame = peekFrame(frameId)
        if (frame === undefined) {
          const error: TransportError = {
            kind: 'validation', code: 'frame-released', message: 'Кадр для поиска уже снят: выберите фото ещё раз', correlationId: '', retryable: false,
          }
          searchRun.finish('failed')
          return { error }
        }
        const form = new FormData()
        form.append('photo', frame, frame instanceof File ? frame.name : 'photo')
        form.append('limit', String(limit))
        for (const resourceId of resourceIds ?? []) form.append('resourceIds', resourceId)
        searchRun.beginSend({ name: frame instanceof File ? frame.name : null, size: frame.size })

        const request: TransportRequest = {
          url: PLATFORM_SEARCH_URLS.photo, method: 'POST', formData: form, responseType: 'ndjson', readOnly: true, noRetry: true, timeoutMs: SEARCH_TIMEOUT_MS,
        }
        // Итог потока копится в объекте: колбэк пишет в него, код после `await` читает — тип без приведений.
        const stream: { terminal: StreamEvent | null; broken: string | null } = { terminal: null, broken: null }
        const handle = (raw: unknown) => {
          try {
            const event = decodeStreamEvent(raw)
            if (event.type === 'result' || event.type === 'error') stream.terminal = event
            else if (event.type === 'preliminary') searchRun.setPreliminary(event.result, event.refining)
            else searchRun.apply(event)
          } catch (failure) {
            stream.broken = failure instanceof Error ? failure.message : String(failure)
          }
        }
        const result = await api.dispatch(
          transportRequest<unknown[]>(request, api.signal, {
            onUploadProgress: (loaded, total) => searchRun.uploadProgress(loaded, total),
            onLine: handle,
          }),
        )
        if (!result.ok) {
          searchRun.finish(result.error.code === 'aborted' ? 'cancelled' : 'failed')
          return { error: result.error }
        }
        const last = stream.terminal
        if (last?.type === 'result') {
          searchRun.finish('done')
          searchRun.setOutcome({ processing: last.result.processing, visualContrast: last.result.visualContrast })
          return { data: last.result }
        }
        searchRun.finish('failed')
        if (last?.type === 'error') return { error: streamFailure(last.problem) }
        const error: TransportError = {
          kind: 'invalid-response',
          code: 'stream-incomplete',
          message: stream.broken === null ? 'Поток поиска оборвался до итога: сервер не прислал ни результата, ни ошибки' : `Сервер прислал событие в неожиданном виде: ${stream.broken}`,
          correlationId: result.correlationId,
          retryable: true,
        }
        return { error }
      },
    }),
    /**
     * Подготовка поиска (RP-FB52): зовётся при открытии сканера и экрана поиска по фото, чтобы служба текста была «тёплой» к первому
     * снимку. Идемпотентна, сервер ограничивает частоту. Сбой подготовки человеку не показывается: поиск скажет сам, если служба недоступна.
     */
    platformSearchWarmup: build.mutation<unknown, void>({
      query: () => ({ url: PLATFORM_SEARCH_URLS.warmup, method: 'POST', body: {} }),
    }),
    /**
     * «Это он» (обучение поиска): сервер запоминает прочитанный текст как поисковые фразы ЭТОГО товара. Идемпотентно по `operationId`.
     * Право — как у правки товара (`platform:catalog:write`).
     */
    platformSearchConfirm: build.mutation<PlatformSearchConfirmResult, PlatformSearchConfirmArgs>({
      query: ({ operationId, productId, words }) => ({ url: PLATFORM_SEARCH_URLS.confirm, method: 'POST', body: { operationId, productId, words } }),
    }),
    /** Метки для обучения поиска по действию человека (RP-FB77). Идемпотентно по `operationId`; право — как у поиска по фото. */
    platformSearchFeedback: build.mutation<PlatformSearchFeedbackResult, PlatformSearchFeedbackArgs>({
      query: ({ operationId, searchId, action, productId }) => ({
        url: PLATFORM_SEARCH_URLS.feedback,
        method: 'POST',
        body: { operationId, searchId, action, ...(productId === undefined ? {} : { productId }) },
      }),
    }),
    /** Сводка времени боевых поисков по этапам за последние 7 дней (период по умолчанию сервера); только системному администратору. */
    platformSearchTimingSummary: build.query<SearchTimingSummary, void>({
      query: () => ({ url: PLATFORM_SEARCH_URLS.timings, decode: decodeSearchTimingSummary }),
      // Сводка меняется с каждым поиском, но смотрят её редко: держим недолго.
      keepUnusedDataFor: 30,
    }),
    /** Время одного поиска по этапам; записи нет (тестовая область, истёк срок) — 404. Время законченного поиска не меняется. */
    platformSearchTimingOf: build.query<SearchTiming, string>({
      query: (searchId) => ({ url: PLATFORM_SEARCH_URLS.timingOf(searchId), decode: decodeSearchTiming }),
    }),
    platformSearchHealth: build.query<SearchHealth, void>({
      query: () => ({ url: PLATFORM_SEARCH_URLS.health, decode: decodeSearchHealth }),
      // Состояние служб устаревает быстро: хранится недолго.
      keepUnusedDataFor: 10,
      providesTags: [{ type: PLATFORM_SEARCH_TAG, id: 'HEALTH' }],
    }),
  }),
})

export const {
  platformSearchPhoto,
  platformSearchHealth,
  platformSearchWarmup,
  platformSearchConfirm,
  platformSearchFeedback,
  platformSearchTimingSummary,
  platformSearchTimingOf,
} = searchApi.endpoints
